import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const pruningSparsityComponent: ComponentDef = {
  id: 'pruning-sparsity',
  name: 'Pruning & Sparsity',
  description: 'Remove or zero-out small weights to reduce model size and accelerate inference.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Method',
      type: 'select',
      default: 'overview',
      options: [
        { label: 'Overview', value: 'overview' },
        { label: 'Magnitude Pruning', value: 'magnitude' },
        { label: 'Structured 2:4', value: 'structured' },
        { label: 'Movement Pruning', value: 'movement' },
        { label: 'Method Comparison', value: 'method_comparison' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    if (variant === 'overview') {
      // High-level pruning pipeline: dense weights -> importance scoring -> pruning mask -> sparse weights -> fine-tune -> output
      g.linear('dense', 'x', [d_model, d_model]);
      g.scale('importance', 'dense', 'Importance Scoring');
      g.scale('prune_mask', 'importance', 'Pruning Mask');
      g.mul('sparse_w', 'dense', 'prune_mask');
      g.scale('finetune', 'sparse_w', 'Fine-tune');
      g.matmul('out', 'x', 'finetune', [seq_len, d_model]);
    } else if (variant === 'method_comparison') {
      // Side-by-side: magnitude, 2:4 structured, movement pruning
      g.linear('dense', 'x', [d_model, d_model]);
      g.split('methods', 'dense', [seq_len, d_model], '3 methods');

      // Magnitude pruning branch
      g.scale('mag_score', 'methods', '|W| Magnitude');
      g.scale('mag_mask', 'mag_score', 'Threshold Mask');
      g.mul('mag_sparse', 'methods', 'mag_mask');

      // Structured 2:4 branch
      g.scale('struct_score', 'methods', '2:4 Block Score');
      g.scale('struct_mask', 'struct_score', 'N:M Pattern Mask');
      g.mul('struct_sparse', 'methods', 'struct_mask');

      // Movement pruning branch
      g.scale('mov_score', 'methods', 'Gradient Movement');
      g.scale('mov_mask', 'mov_score', 'Adaptive Mask');
      g.mul('mov_sparse', 'methods', 'mov_mask');

      g.concat('results', ['mag_sparse', 'struct_sparse', 'mov_sparse'], [3, d_model]);
      g.scale('compare', 'results', 'Compare Sparsity');
      g.linear('out', 'compare', [d_model, d_model]);
    } else if (variant === 'structured') {
      // Structured 2:4 pruning
      g.linear('dense', 'x', [d_model, d_model]);
      g.scale('block_score', 'dense', '2:4 Block Score');
      g.scale('nm_mask', 'block_score', 'N:M Pattern Mask');
      g.mul('sparse_w', 'dense', 'nm_mask');
      g.matmul('out', 'x', 'sparse_w', [seq_len, d_model]);
    } else if (variant === 'movement') {
      // Movement pruning
      g.linear('dense', 'x', [d_model, d_model]);
      g.scale('grad_score', 'dense', 'Gradient Score');
      g.scale('mov_mask', 'grad_score', 'Movement Mask');
      g.mul('sparse_w', 'dense', 'mov_mask');
      g.matmul('out', 'x', 'sparse_w', [seq_len, d_model]);
    } else {
      // Magnitude pruning (default fallback)
      g.linear('dense', 'x', [d_model, d_model]);
      g.scale('importance', 'dense', 'Importance');
      g.scale('mask', 'importance', 'Sparsity Mask');
      g.mul('sparse_w', 'dense', 'mask');
      g.matmul('out', 'x', 'sparse_w', [seq_len, d_model]);
    }

    g.output('out', 'out');
    return g.build();
  }
};
